import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { Obra } from "../../domain/models/obra";
import { useLocation } from "react-router";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { useObrasGestaoServicosFilter } from "../hooks/Obras_gestao_servicos/filter";
import { IncorporacaoBlocoDropDown } from "../../domain/models/incorporacao_bloco_dropdown";
import { IncorporacaoBloco } from "../../domain/models/incorporacao_bloco";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";

import CommonConstants from "../../../../common/constants";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { Empreendimento } from "../../../incorporacao/domain/models/empreendimento";

/**
 * LoadObraFuncState
 */

type LoadObraFuncState = {
  idObra?: number;
};

/**
 * LoadEmpreendimentoFuncState
 */

type LoadEmpreendimentoFuncState = {
  idObra?: number;
};

/**
 * useObrasGestaoServicosViewModel
 */

export const useObrasGestaoServicosViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
    },
    obras: {
      getObraHeaderInfoUseCase,
      getEmpreendimentoDropDownUseCase,
      getIncorporacaoBlocoDropDownUseCase,
      getObraByIdUseCase,
      getEmpreendimentoByIdObraUseCase,
      getIncorporacaoBlocosByIdListaUseCase,
    },
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();
  const [selectedIdObra, setSelectedIdObra] = useState<number>();
  const [selectedObra, setSelectedObra] = useState<Obra>();
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState<Empreendimento>();
  const [selectedIncorporacaoBlocoDropDown, setSelectedIncorporacaoBlocoDropDown] = useState<IncorporacaoBlocoDropDown[]>();
  const [selectedIncorporacaoBlocoLista, setSelectedIncorporacaoBlocoLista] = useState<IncorporacaoBloco[]>();

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra: selectedIdObra });

  const filters = useObrasGestaoServicosFilter();

  const selectedEmpreendimentoDropDown = useMemo(() => {
    if (!filters.empreendimentosDropDown || !selectedObra) return null;
    return filters.empreendimentosDropDown.find(x => x.idEmpreendimento === selectedObra.idEmpreendimento) ?? null;
  }, [
    filters.empreendimentosDropDown,
    selectedObra,
  ]);

  const selectedCentroCustoDropDown = useMemo(() => {
    if (!filters.centroCustosDropDown || !selectedObra) return null;
    return filters.centroCustosDropDown.find(x => x.id === selectedObra.idCentroCusto) ?? null;
  }, [
    filters.centroCustosDropDown,
    selectedObra,
  ]);

  /**
   * Funções de carregamento
   */

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? selectedIdObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    selectedIdObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadEmpreendimentosDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.empreendimentoDropDown,
    loader: async () => await getEmpreendimentoDropDownUseCase.getEmpreendimentoDropdown(),
    onSet: (dt) => useObrasGestaoServicosFilter.setState({ empreendimentosDropDown: dt }),
    onError: e => { throw e; },
  });

  const loadIncorporacaoBlocoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.incorporacaoBlocoDropDown,
    loader: async (idEmpreendimento) => await getIncorporacaoBlocoDropDownUseCase.getIncorporacaoBlocoDropdown(idEmpreendimento),
    onSet: async (dt) => {
      /*if (dt.length > 0) {
        const idIncorporacaoBlocoLista = dt.map(x => x.idIncorporacaoBloco);
        const incorporacaoBlocos = await getIncorporacaoBlocosByIdListaUseCase
          .getIncorporacaoBlocosByIdLista(idIncorporacaoBlocoLista);
        setSelectedIncorporacaoBlocoLista(incorporacaoBlocos);
        setSelectedIncorporacaoBlocoDropDown(dt);
      }*/
      useObrasGestaoServicosFilter.setState({ incorporacaoBlocosDropDown: dt })
    },
    onError: e => { throw e; },
  });

  const loadCentroCustoDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () => await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
    onSet: (dt) => useObrasGestaoServicosFilter.setState({ centroCustosDropDown: dt }),
    onError: e => { throw e; },
  });

  const loadEmpreendimento = useCallback(async (funcState?: LoadEmpreendimentoFuncState) => {
    try {
      const filteredIdObra = funcState?.idObra ?? selectedIdObra;

      const empreendimento = await getEmpreendimentoByIdObraUseCase.getEmpreendimentoByIdObra(filteredIdObra);
      setSelectedEmpreendimento(empreendimento);

      await loadIncorporacaoBlocoDropdown(empreendimento.idEmpreendimento);
    } catch (e: unknown) {
      setSelectedEmpreendimento(undefined);
      throw e;
    }
  }, [
    selectedIdObra,
    getEmpreendimentoByIdObraUseCase,
    loadIncorporacaoBlocoDropdown,
  ]);

  const loadObra = useCallback(async (funcState?: LoadObraFuncState) => {
    try {
      const filteredIdObra = funcState?.idObra ?? selectedIdObra;

      const obra = await getObraByIdUseCase.getObraById(
        TelasIds.Obras_gestao_servicos,
        filteredIdObra,
      );
      setSelectedObra(obra);
    } catch (e: unknown) {
      setSelectedObra(undefined);
      throw e;
    }
  }, [
    selectedIdObra,
    getObraByIdUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setSelectedIdObra(Number(id));

      await loadEmpreendimentosDropDown();
      await loadCentroCustoDropDown();
      await loadObraHeaderInfo(Number(id));
      await loadObrasCommonReportsData(Number(id));
      await loadObra({ idObra: Number(id) });
      await loadEmpreendimento({ idObra: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadEmpreendimentosDropDown();
      await loadCentroCustoDropDown();
      await loadObraHeaderInfo();
      await loadObra();
      await loadEmpreendimento();
      await loadObrasCommonReportsData();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadEmpreendimentosDropDown,
    loadCentroCustoDropDown,
    loadObraHeaderInfo,
    loadObra,
    loadEmpreendimento,
    loadObrasCommonReportsData,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleIncorporacaoBlocoDropDownChange = useCallback(async (incorporacaoBlocosDropdown: IncorporacaoBlocoDropDown[] | null) => {
    try {
      setLoading(true);
      if (incorporacaoBlocosDropdown && incorporacaoBlocosDropdown.length > 0) {
        const idIncorporacaoBlocoLista = incorporacaoBlocosDropdown.map(x => x.idIncorporacaoBloco);
        const incorporacaoBlocos = await getIncorporacaoBlocosByIdListaUseCase
          .getIncorporacaoBlocosByIdLista(idIncorporacaoBlocoLista);
        setSelectedIncorporacaoBlocoLista(incorporacaoBlocos);
      } else {
        setSelectedIncorporacaoBlocoLista(undefined);
      }
    } catch (e: unknown) {
      setSelectedIncorporacaoBlocoLista(undefined);
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar blocos de incorporação!'));
    } finally {
      setLoading(false);
    }
    setSelectedIncorporacaoBlocoDropDown(incorporacaoBlocosDropdown ?? undefined);
  }, [
    getIncorporacaoBlocosByIdListaUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    errorMessage,
    selectedIdObra: selectedIdObra ?? null,
    selectedObra: selectedObra ?? null,

    obrasReportState,
    obrasTopHeaderState,
    selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,

    filters,

    selectedEmpreendimentoDropDown,
    selectedCentroCustoDropDown,
    selectedIncorporacaoBlocoDropDown,
    selectedEmpreendimento,
    selectedIncorporacaoBlocoLista: selectedIncorporacaoBlocoLista ?? null,

    dropdown: {
      empreendimentosDropDown: filters.empreendimentosDropDown ?? [],
      incorporacaoBlocosDropDown: filters.incorporacaoBlocosDropDown ?? [],
      centroCustosDropDown: filters.centroCustosDropDown ?? [],
    },

    handleReload,
    handleBack,
    handleIncorporacaoBlocoDropDownChange,
  };
}